import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  getInvoicesAndPageInfo,
  mergeInvoicesResults,
} from './useInvoices.helpers';
import {
  GET_INVOICES_LIST,
  InvoicesResponse,
  InvoicesVariables,
  InvoiceStatus,
} from '@/features/Profile/sections/Invoices/api';
import { InvoiceStatusFilter } from '@/features/Profile/sections/Invoices/Invoices.types';
import { DEFAULT_PAGE_SIZE } from '@/features/Profile/sections/Invoices/Invoices.constants';
import { useOnlineStatus } from '@/features/Profile/core/hooks';

function getStatusVariable(
  statusFilter: InvoiceStatusFilter
): InvoiceStatus | undefined {
  return statusFilter === InvoiceStatusFilter.All
    ? undefined
    : (statusFilter as InvoiceStatus);
}

export function useInvoices(statusFilter: InvoiceStatusFilter, skip = false) {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isOnline = useOnlineStatus();
  const status = getStatusVariable(statusFilter);

  const { data, loading, fetchMore } = useQuery<
    InvoicesResponse,
    InvoicesVariables
  >(GET_INVOICES_LIST, {
    fetchPolicy: 'cache-and-network',
    variables: { first: DEFAULT_PAGE_SIZE, after: null, status },
    skip,
  });

  const { invoices, pageInfo } = getInvoicesAndPageInfo(data);
  const { endCursor, hasNextPage = false } = pageInfo ?? {};
  const canLoadMore = hasNextPage && isOnline;

  const loadMore = useCallback(() => {
    if (loading || isLoadingMore || !canLoadMore || !endCursor) {
      return;
    }

    setIsLoadingMore(true);
    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prevResult, options) =>
        mergeInvoicesResults(prevResult, options),
    }).finally(() => setIsLoadingMore(false));
  }, [canLoadMore, endCursor, fetchMore, isLoadingMore, loading]);

  return {
    invoices,
    isLoading: loading || skip,
    isLoadingMore,
    hasNextPage,
    canLoadMore,
    loadMore,
  };
}
